/**
 * VÉRAULT Portal — Client: Destinations View (Wave 26.6)
 *
 * Read-only display of destination segments + per-stop weather, for the
 * client portal. Honors `visible_on_client` filter (server-enforced).
 *
 * Layout: editorial card list + Leaflet map (OSM tiles, no key).
 */
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Loader2, Cloud } from "lucide-react";
import { api, Heading, fmt } from "../../index";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const WEATHER_ICONS = {
  0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️", 45: "🌫️", 48: "🌫️",
  51: "🌦️", 53: "🌦️", 55: "🌧️",
  61: "🌧️", 63: "🌧️", 65: "🌧️",
  71: "🌨️", 73: "🌨️", 75: "❄️",
  80: "🌦️", 81: "🌧️", 82: "⛈️",
  95: "⛈️", 96: "⛈️", 99: "⛈️",
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 6, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

function WeatherCard({ segment }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!segment?.latitude || !segment?.longitude) return;
    let mounted = true;
    setLoading(true);
    api.get(`/weather/forecast?lat=${segment.latitude}&lng=${segment.longitude}`)
      .then(({ data }) => { if (mounted) setForecast(data?.forecasts || []); })
      .catch(() => mounted && setForecast([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [segment?.latitude, segment?.longitude]);

  const days = useMemo(() => {
    if (!forecast) return [];
    const start = segment.segment_start_date?.slice(0, 10);
    const end = segment.segment_end_date?.slice(0, 10);
    if (!start || !end) return forecast.slice(0, 7);
    const inRange = forecast.filter((f) => f.date >= start && f.date <= end);
    return inRange.length > 0 ? inRange : forecast.slice(0, 7);
  }, [forecast, segment]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--foreground-muted))] font-mono uppercase tracking-[0.18em]">
        <Loader2 className="size-3.5 animate-spin" /> Reading the sky…
      </div>
    );
  }
  if (!days.length) return null;

  return (
    <div className="mt-3 min-w-0" data-testid="client-weather-strip">
      <div className="flex items-center gap-2 mb-2">
        <Cloud className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          Forecast — during your stay
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory scroll-px-1">
        {days.map((d) => {
          const dateObj = new Date(d.date);
          const day = dateObj.toLocaleDateString("en-US", { weekday: "short" });
          const mon = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          // Wave 11g · Phase B+ — USA-first display. Open-Meteo returns °C; convert to °F here.
          const cToF = (c) => Math.round((c * 9) / 5 + 32);
          return (
            <div
              key={d.date}
              className="flex-shrink-0 snap-start px-2.5 py-2 rounded-md bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.04)] min-w-[64px] text-center"
              title={`${Math.round(d.temp_high)}°C / ${Math.round(d.temp_low)}°C`}
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-[hsl(var(--foreground-muted))]">{day}</div>
              <div className="font-mono text-[9px] text-[hsl(var(--foreground-muted))]">{mon}</div>
              <div className="text-[18px] my-0.5">{WEATHER_ICONS[d.weather_code] || "•"}</div>
              <div className="font-serif text-[12px]">
                <span className="text-[hsl(var(--foreground))]">{cToF(d.temp_high)}°</span>
                <span className="text-[hsl(var(--foreground-muted))] mx-0.5">/</span>
                <span className="text-[hsl(var(--foreground-muted))]">{cToF(d.temp_low)}°</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DestinationMap({ segments }) {
  const points = useMemo(
    () => segments.filter((s) => s.latitude && s.longitude).map((s) => ({
      id: s.id, lat: s.latitude, lng: s.longitude, label: s.destination_name,
    })),
    [segments]
  );
  if (points.length === 0) return null;
  return (
    <div className="rounded-md overflow-hidden border border-[rgba(0,0,0,0.08)] min-w-0 w-full" data-testid="client-destinations-map">
      <MapContainer center={[points[0].lat, points[0].lng]} zoom={4} scrollWheelZoom={false} style={{ height: 280, width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]}>
            <Popup><div className="font-serif text-[14px]">{p.label}</div></Popup>
          </Marker>
        ))}
        {points.length > 1 && (
          <Polyline positions={points.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#C8A85A", weight: 2, dashArray: "6, 6", opacity: 0.8 }} />
        )}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}

export function ClientDestinationsView({ tripId }) {
  const [segments, setSegments] = useState(null);

  useEffect(() => {
    if (!tripId) return;
    let mounted = true;
    api.get(`/client-portal/trips/${tripId}/destinations`)
      .then(({ data }) => { if (mounted) setSegments(data?.destinations || []); })
      .catch(() => mounted && setSegments([]));
    return () => { mounted = false; };
  }, [tripId]);

  if (segments === null) {
    return <div className="text-[12px] text-[hsl(var(--foreground-muted))] font-mono uppercase tracking-[0.18em]"><Loader2 className="size-3.5 animate-spin inline mr-1" /> Tracing the route…</div>;
  }
  if (segments.length === 0) return null;

  return (
    <div className="space-y-5" data-testid="client-destinations">
      <div>
        <Heading level="eyebrow">Where you'll be</Heading>
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] mt-1">
          {segments.length} stop{segments.length === 1 ? "" : "s"} · Forecast updates daily.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-3 min-w-0">
          {segments.map((s) => (
            <div key={s.id} className="border border-[rgba(0,0,0,0.06)] rounded-md p-4 bg-white min-w-0 overflow-hidden" data-testid="client-destination-card">
              <div className="flex items-center gap-2">
                <MapPin className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                <span className="font-serif text-[16px] text-[hsl(var(--foreground))]">{s.destination_name}</span>
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                {fmt.formatDateRange(s.segment_start_date, s.segment_end_date)}
              </div>
              {s.country && (
                <div className="mt-0.5 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">{s.country}</div>
              )}
              <WeatherCard segment={s} />
            </div>
          ))}
        </div>
        <DestinationMap segments={segments} />
      </div>
    </div>
  );
}
